import { DeleteOutlined, ExclamationCircleOutlined, PlusOutlined } from "@ant-design/icons";
import { Input, Popover, Table, Tooltip } from "antd";
import type { InputRef } from "antd";
import type { ColumnsType } from "antd/es/table";
import { AppButton, AppTable } from "components/antd-ui";
import { TableLayout } from "components/table";
import { InputNumber } from "components/Input/InputNumber";
import {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ChangeEvent,
  type KeyboardEvent,
} from "react";
import { formatMoney, formatQuantity } from "../opportunity.meta";
import "./ProductTable.scss";

export type ProductRow = {
  key: string;
  id?: number;
  description: string;
  unit: string;
  quantity: number | null;
  price: number | null;
  vatPercent: number | null;
  // Chiết khấu đối tác trên mỗi đơn vị (đã nằm trong price); chỉ dùng khi showPartner
  partnerDiscount: number | null;
};

type ProductField = "description" | "unit" | "quantity" | "price" | "vatPercent" | "partnerDiscount";

// Thứ tự ô theo cột (phím Tab): CK đối tác (nếu có) đứng trước Đơn giá
const fieldsFor = (showPartner?: boolean): ProductField[] => [
  "description",
  "unit",
  "quantity",
  ...(showPartner ? (["partnerDiscount"] as ProductField[]) : []),
  "price",
  "vatPercent",
];

type EditingCell = {
  key: string;
  field: ProductField;
};

type Patch = (key: string, patch: Partial<ProductRow>) => void;

type CellEditProps = {
  editing: boolean;
  onStart: () => void;
  onStop: () => void;
  onTab: (backward: boolean) => void;
};

const stepCell = (rows: ProductRow[], current: EditingCell, delta: 1 | -1, fields: ProductField[]): EditingCell | null => {
  const rowIndex = rows.findIndex((row) => row.key === current.key);
  const fieldIndex = fields.indexOf(current.field);
  if (rowIndex < 0 || fieldIndex < 0) return null;

  let nextRow = rowIndex;
  let nextField = fieldIndex + delta;
  if (nextField >= fields.length) {
    nextRow += 1;
    nextField = 0;
  } else if (nextField < 0) {
    nextRow -= 1;
    nextField = fields.length - 1;
  }

  if (nextRow < 0 || nextRow >= rows.length) return null;
  return { key: rows[nextRow].key, field: fields[nextField] };
};

type ProductTableProps = {
  rows: ProductRow[];
  readOnly?: boolean;
  // Hiện cột chiết khấu đối tác: chỉ truyền true cho admin / sale phụ trách khách
  showPartner?: boolean;
  onChange: (rows: ProductRow[]) => void;
};

const ClickEditText = memo(
  ({
    value,
    readOnly,
    editing,
    onStart,
    onStop,
    onTab,
    onChange,
  }: {
    value: string;
    readOnly?: boolean;
    onChange: (value: string) => void;
  } & CellEditProps) => {
    const inputRef = useRef<InputRef>(null);

    useEffect(() => {
      if (editing) {
        inputRef.current?.focus();
      }
    }, [editing]);

    const handleChange = useCallback(
      (event: ChangeEvent<HTMLInputElement>) => {
        onChange(event.target.value);
      },
      [onChange],
    );

    const handleKeyDown = useCallback(
      (event: KeyboardEvent<HTMLInputElement>) => {
        if (event.key !== "Tab") return;
        event.preventDefault();
        onTab(event.shiftKey);
      },
      [onTab],
    );

    if (readOnly || !editing) {
      return (
        <button type="button" className="clickEditCell" onClick={onStart} disabled={readOnly}>
          {value || "\u00a0"}
        </button>
      );
    }

    return (
      <Input
        ref={inputRef}
        className="clickEditCell__input"
        value={value}
        onChange={handleChange}
        onBlur={onStop}
        onPressEnter={onStop}
        onKeyDown={handleKeyDown}
      />
    );
  },
);

ClickEditText.displayName = "ClickEditText";

const ClickEditNumber = memo(
  ({
    value,
    readOnly,
    editing,
    onStart,
    onStop,
    onTab,
    onChange,
  }: {
    value: number | null;
    readOnly?: boolean;
    onChange: (value: number | null) => void;
  } & CellEditProps) => {
    const wrapRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
      if (editing) {
        wrapRef.current?.querySelector("input")?.focus();
      }
    }, [editing]);

    const handleChange = useCallback(
      (event: ChangeEvent<HTMLInputElement>) => {
        const raw = event.currentTarget.value.replaceAll(".", "");
        if (raw === "") {
          onChange(null);
          return;
        }
        const numeric = Number(raw);
        onChange(Number.isNaN(numeric) ? null : numeric);
      },
      [onChange],
    );

    const handleKeyDown = useCallback(
      (event: KeyboardEvent<HTMLInputElement>) => {
        if (event.key !== "Tab") return;
        event.preventDefault();
        onTab(event.shiftKey);
      },
      [onTab],
    );

    if (readOnly || !editing) {
      return (
        <button type="button" className="clickEditCell isRight" onClick={onStart} disabled={readOnly}>
          {value === null ? "\u00a0" : formatQuantity(value)}
        </button>
      );
    }

    return (
      <div ref={wrapRef}>
        <InputNumber
          className="clickEditCell__input"
          value={value ?? ""}
          onChange={handleChange}
          onBlur={onStop}
          onPressEnter={onStop}
          onKeyDown={handleKeyDown}
        />
      </div>
    );
  },
);

ClickEditNumber.displayName = "ClickEditNumber";

type FieldCellProps = {
  row: ProductRow;
  readOnly?: boolean;
  onPatch: Patch;
} & CellEditProps;

const DescriptionCell = memo(({ row, readOnly, editing, onStart, onStop, onTab, onPatch }: FieldCellProps) => {
  const onChange = useCallback(
    (value: string) => onPatch(row.key, { description: value }),
    [onPatch, row.key],
  );
  return (
    <ClickEditText
      value={row.description}
      readOnly={readOnly}
      editing={editing}
      onStart={onStart}
      onStop={onStop}
      onTab={onTab}
      onChange={onChange}
    />
  );
});
DescriptionCell.displayName = "DescriptionCell";

const UnitCell = memo(({ row, readOnly, editing, onStart, onStop, onTab, onPatch }: FieldCellProps) => {
  const onChange = useCallback((value: string) => onPatch(row.key, { unit: value }), [onPatch, row.key]);
  return (
    <ClickEditText
      value={row.unit}
      readOnly={readOnly}
      editing={editing}
      onStart={onStart}
      onStop={onStop}
      onTab={onTab}
      onChange={onChange}
    />
  );
});
UnitCell.displayName = "UnitCell";

const QuantityCell = memo(({ row, readOnly, editing, onStart, onStop, onTab, onPatch }: FieldCellProps) => {
  const onChange = useCallback(
    (value: number | null) => onPatch(row.key, { quantity: value }),
    [onPatch, row.key],
  );
  return (
    <ClickEditNumber
      value={row.quantity}
      readOnly={readOnly}
      editing={editing}
      onStart={onStart}
      onStop={onStop}
      onTab={onTab}
      onChange={onChange}
    />
  );
});
QuantityCell.displayName = "QuantityCell";

const PriceCell = memo(({ row, readOnly, editing, onStart, onStop, onTab, onPatch }: FieldCellProps) => {
  const onChange = useCallback(
    (value: number | null) => onPatch(row.key, { price: value }),
    [onPatch, row.key],
  );
  return (
    <ClickEditNumber
      value={row.price}
      readOnly={readOnly}
      editing={editing}
      onStart={onStart}
      onStop={onStop}
      onTab={onTab}
      onChange={onChange}
    />
  );
});
PriceCell.displayName = "PriceCell";

const VatPercentCell = memo(({ row, readOnly, editing, onStart, onStop, onTab, onPatch }: FieldCellProps) => {
  const onChange = useCallback(
    (value: number | null) => onPatch(row.key, { vatPercent: value }),
    [onPatch, row.key],
  );
  return (
    <ClickEditNumber
      value={row.vatPercent}
      readOnly={readOnly}
      editing={editing}
      onStart={onStart}
      onStop={onStop}
      onTab={onTab}
      onChange={onChange}
    />
  );
});
VatPercentCell.displayName = "VatPercentCell";

// Chiết khấu đối tác: chỉ nhập SỐ TIỀN trên mỗi đơn vị; % tự tính từ số tiền / đơn giá, hiện chữ nhỏ bên dưới
const partnerPercentOf = (row: ProductRow) =>
  row.price && row.partnerDiscount ? Math.round((row.partnerDiscount / row.price) * 10000) / 100 : 0;

const PartnerDiscountCell = memo(({ row, readOnly, editing, onStart, onStop, onTab, onPatch }: FieldCellProps) => {
  const onChange = useCallback(
    (value: number | null) => onPatch(row.key, { partnerDiscount: value }),
    [onPatch, row.key],
  );
  return (
    <div className="productTable__partnerCell">
      {partnerPercentOf(row) > 0 && (
        <Tooltip title={`Tương đương ${String(partnerPercentOf(row)).replace(".", ",")}% đơn giá`}>
          <span className="productTable__percentTag">{String(partnerPercentOf(row)).replace(".", ",")}%</span>
        </Tooltip>
      )}
      <div className="productTable__partnerInput">
        <ClickEditNumber
          value={row.partnerDiscount}
          readOnly={readOnly}
          editing={editing}
          onStart={onStart}
          onStop={onStop}
          onTab={onTab}
          onChange={onChange}
        />
      </div>
    </div>
  );
});
PartnerDiscountCell.displayName = "PartnerDiscountCell";

const RemoveCell = memo(
  ({ row, onRemove }: { row: ProductRow; onRemove: (key: string) => void }) => {
    const remove = useCallback(() => onRemove(row.key), [onRemove, row.key]);
    return (
      <button type="button" className="productTable__remove" onClick={remove} aria-label="Xóa dòng">
        <DeleteOutlined />
      </button>
    );
  },
);
RemoveCell.displayName = "RemoveCell";

const AmountCell = memo(({ row }: { row: ProductRow }) => {
  const amount = (row.quantity || 0) * (row.price || 0);
  return <span className="productTable__amount">{formatMoney(amount)}</span>;
});
AmountCell.displayName = "AmountCell";

const AmountWithVatCell = memo(({ row }: { row: ProductRow }) => {
  const amount = (row.quantity || 0) * (row.price || 0);
  const amountWithVat = Math.round(amount * (1 + (row.vatPercent || 0) / 100));
  return <span className="productTable__amount">{formatMoney(amountWithVat)}</span>;
});
AmountWithVatCell.displayName = "AmountWithVatCell";

export const createProductRow = (): ProductRow => ({
  key: `new-${Date.now()}-${Math.random().toString(16).slice(2)}`,
  description: "",
  unit: "",
  quantity: null,
  price: null,
  vatPercent: 0,
  partnerDiscount: 0,
});

const isEditing = (editingCell: EditingCell | null, key: string, field: ProductField) =>
  editingCell?.key === key && editingCell.field === field;

export const ProductTable = memo(({ rows, readOnly, showPartner, onChange }: ProductTableProps) => {
  const [editingCell, setEditingCell] = useState<EditingCell | null>(null);
  const addWrapRef = useRef<HTMLDivElement>(null);
  const focusAddRef = useRef(false);

  const onPatch = useCallback(
    (key: string, patch: Partial<ProductRow>) => {
      onChange(rows.map((row) => (row.key === key ? { ...row, ...patch } : row)));
    },
    [onChange, rows],
  );

  const onRemove = useCallback(
    (key: string) => {
      setEditingCell((current) => (current?.key === key ? null : current));
      const next = rows.filter((row) => row.key !== key);
      onChange(next.length ? next : [createProductRow()]);
    },
    [onChange, rows],
  );

  const addRow = useCallback(() => {
    onChange([...rows, createProductRow()]);
  }, [onChange, rows]);

  const startEdit = useCallback(
    (key: string, field: ProductField) => {
      if (readOnly) return;
      setEditingCell({ key, field });
    },
    [readOnly],
  );

  const stopEdit = useCallback((key: string, field: ProductField) => {
    setEditingCell((current) => (current?.key === key && current.field === field ? null : current));
  }, []);

  const moveEdit = useCallback(
    (key: string, field: ProductField, backward: boolean) => {
      const next = stepCell(rows, { key, field }, backward ? -1 : 1, fieldsFor(showPartner));
      if (next) {
        setEditingCell(next);
        return;
      }
      focusAddRef.current = !backward;
      setEditingCell(null);
    },
    [rows],
  );

  useEffect(() => {
    if (editingCell !== null || !focusAddRef.current) return;
    focusAddRef.current = false;
    addWrapRef.current?.querySelector("button")?.focus();
  }, [editingCell]);

  const sumQuantity = rows.reduce((sum, row) => sum + (row.quantity || 0), 0);
  // Tổng tiền hàng (chưa thuế) — KHÔNG cộng VAT vào đây, vì đây là số dùng tính % đợt thu công nợ /
  // hoa hồng sale ở màn Đơn hàng. VAT chỉ hiển thị thêm, không được phép lẫn vào số này.
  const sumAmount = rows.reduce((sum, row) => sum + (row.quantity || 0) * (row.price || 0), 0);
  // Cộng dồn từng dòng đã làm tròn về đồng (khớp cách backend tính), rồi mới suy ra tiền thuế —
  // tránh lệch thập phân do cộng dồn các số lẻ dấu phẩy động.
  const sumAmountWithVat = rows.reduce(
    (sum, row) => sum + Math.round((row.quantity || 0) * (row.price || 0) * (1 + (row.vatPercent || 0) / 100)),
    0,
  );
  const sumVat = sumAmountWithVat - sumAmount;
  // Tổng chiết khấu đối tác = Σ số lượng × chiết khấu trên đơn vị
  const sumPartner = Math.round(rows.reduce((sum, row) => sum + (row.quantity || 0) * (row.partnerDiscount || 0), 0));

  const columns = useMemo<ColumnsType<ProductRow>>(() => {
    const next: ColumnsType<ProductRow> = [
      {
        title: "Diễn giải",
        dataIndex: "description",
        sorter: false,
        // Cột tự giãn phần còn lại nhưng không hẹp hơn 280px
        onHeaderCell: () => ({ style: { minWidth: 280 } }),
        onCell: () => ({ style: { minWidth: 280 } }),
        render: (_, row) => (
          <DescriptionCell
            row={row}
            readOnly={readOnly}
            editing={isEditing(editingCell, row.key, "description")}
            onPatch={onPatch}
            onStart={() => startEdit(row.key, "description")}
            onStop={() => stopEdit(row.key, "description")}
            onTab={(backward) => moveEdit(row.key, "description", backward)}
          />
        ),
      },
      {
        title: "ĐVT",
        dataIndex: "unit",
        width: 90,
        sorter: false,
        render: (_, row) => (
          <UnitCell
            row={row}
            readOnly={readOnly}
            editing={isEditing(editingCell, row.key, "unit")}
            onPatch={onPatch}
            onStart={() => startEdit(row.key, "unit")}
            onStop={() => stopEdit(row.key, "unit")}
            onTab={(backward) => moveEdit(row.key, "unit", backward)}
          />
        ),
      },
      {
        title: "Số lượng",
        dataIndex: "quantity",
        width: 100,
        align: "right",
        sorter: false,
        render: (_, row) => (
          <QuantityCell
            row={row}
            readOnly={readOnly}
            editing={isEditing(editingCell, row.key, "quantity")}
            onPatch={onPatch}
            onStart={() => startEdit(row.key, "quantity")}
            onStop={() => stopEdit(row.key, "quantity")}
            onTab={(backward) => moveEdit(row.key, "quantity", backward)}
          />
        ),
      },
      {
        title: "Đơn giá",
        dataIndex: "price",
        width: 130,
        align: "right",
        sorter: false,
        render: (_, row) => (
          <PriceCell
            row={row}
            readOnly={readOnly}
            editing={isEditing(editingCell, row.key, "price")}
            onPatch={onPatch}
            onStart={() => startEdit(row.key, "price")}
            onStop={() => stopEdit(row.key, "price")}
            onTab={(backward) => moveEdit(row.key, "price", backward)}
          />
        ),
      },
      {
        title: "Thành tiền",
        key: "amount",
        width: 130,
        align: "right",
        render: (_, row) => <AmountCell row={row} />,
      },
      {
        title: "Thuế VAT (%)",
        dataIndex: "vatPercent",
        width: 90,
        align: "right",
        sorter: false,
        render: (_, row) => (
          <VatPercentCell
            row={row}
            readOnly={readOnly}
            editing={isEditing(editingCell, row.key, "vatPercent")}
            onPatch={onPatch}
            onStart={() => startEdit(row.key, "vatPercent")}
            onStop={() => stopEdit(row.key, "vatPercent")}
            onTab={(backward) => moveEdit(row.key, "vatPercent", backward)}
          />
        ),
      },
      {
        title: "Tổng tiền (gồm VAT)",
        key: "amountWithVat",
        width: 140,
        align: "right",
        render: (_, row) => <AmountWithVatCell row={row} />,
      },
    ];

    if (showPartner) {
      const partnerColumn: ColumnsType<ProductRow>[number] = {
        title: (
          <span>
            CK đối tác (đ){" "}
            <Popover
              trigger="click"
              placement="bottom"
              content={
                <div style={{ maxWidth: 320 }}>
                  <b>Chiết khấu đối tác giới thiệu (đ/ĐVT).</b> Nhập số tiền chiết khấu trả cho đối tác trên{" "}
                  <b>mỗi đơn vị</b>. Khoản này <b>đã nằm trong Đơn giá</b> khách thấy, khách không thấy cột này. Hệ thống dùng nó
                  để tính <b>tiền tính hoa hồng</b> (loại phần chiết khấu và thuế ra) và chia phần đối tác theo từng đợt thu.
                </div>
              }
            >
              <ExclamationCircleOutlined className="productTable__hint" aria-label="Giải thích cột chiết khấu đối tác" />
            </Popover>
          </span>
        ),
        key: "partnerDiscount",
        width: 140,
        align: "right",
        render: (_, row) => (
          <PartnerDiscountCell
            row={row}
            readOnly={readOnly}
            editing={isEditing(editingCell, row.key, "partnerDiscount")}
            onPatch={onPatch}
            onStart={() => startEdit(row.key, "partnerDiscount")}
            onStop={() => stopEdit(row.key, "partnerDiscount")}
            onTab={(backward) => moveEdit(row.key, "partnerDiscount", backward)}
          />
        ),
      };
      const priceIndex = next.findIndex((column) => column.key === undefined && (column as { dataIndex?: string }).dataIndex === "price");
      next.splice(priceIndex < 0 ? next.length : priceIndex, 0, partnerColumn);
    }

    if (!readOnly) {
      next.push({
        title: "",
        key: "remove",
        width: 56,
        align: "center",
        render: (_, row) => <RemoveCell row={row} onRemove={onRemove} />,
      });
    }

    return next;
  }, [editingCell, moveEdit, onPatch, onRemove, readOnly, showPartner, startEdit, stopEdit]);

  // CK đối tác thêm đúng 1 cột (trước Đơn giá) nên các ô tổng dịch sang phải 1 ô khi showPartner
  const o = showPartner ? 1 : 0;
  const summary = useCallback(
    () => (
      <>
        <Table.Summary.Row>
          <Table.Summary.Cell index={0} colSpan={2}>
            <span className="productTable__totalLabel">Tổng tiền hàng</span>
          </Table.Summary.Cell>
          <Table.Summary.Cell index={2} align="right">
            {formatQuantity(sumQuantity)}
          </Table.Summary.Cell>
          {showPartner && <Table.Summary.Cell index={3} />}
          <Table.Summary.Cell index={3 + o} />
          <Table.Summary.Cell index={4 + o} />
          <Table.Summary.Cell index={5 + o} />
          <Table.Summary.Cell index={6 + o} align="right">
            {formatMoney(sumAmount)}
          </Table.Summary.Cell>
          {!readOnly && <Table.Summary.Cell index={7 + o} />}
        </Table.Summary.Row>
        <Table.Summary.Row>
          <Table.Summary.Cell index={0} colSpan={6 + o}>
            <span className="productTable__totalLabel">Tiền thuế VAT</span>
          </Table.Summary.Cell>
          <Table.Summary.Cell index={6 + o} align="right">
            {formatMoney(sumVat)}
          </Table.Summary.Cell>
          {!readOnly && <Table.Summary.Cell index={7 + o} />}
        </Table.Summary.Row>
        <Table.Summary.Row>
          <Table.Summary.Cell index={0} colSpan={6 + o}>
            <span className="productTable__totalLabel">Tổng cộng (gồm VAT)</span>
          </Table.Summary.Cell>
          <Table.Summary.Cell index={6 + o} align="right">
            <span className="productTable__totalLabel">{formatMoney(sumAmountWithVat)}</span>
          </Table.Summary.Cell>
          {!readOnly && <Table.Summary.Cell index={7 + o} />}
        </Table.Summary.Row>
        {showPartner && (
          <Table.Summary.Row>
            <Table.Summary.Cell index={0} colSpan={6 + o}>
              <span className="productTable__totalLabel">Tổng chiết khấu đối tác</span>
            </Table.Summary.Cell>
            <Table.Summary.Cell index={6 + o} align="right">
              {formatMoney(sumPartner)}
            </Table.Summary.Cell>
            {!readOnly && <Table.Summary.Cell index={7 + o} />}
          </Table.Summary.Row>
        )}
      </>
    ),
    [o, readOnly, showPartner, sumAmount, sumAmountWithVat, sumPartner, sumQuantity, sumVat],
  );

  return (
    <div className="productTable">
      <TableLayout
        table={
          <AppTable<ProductRow>
            rowKey="key"
            pagination={false}
            dataSource={rows}
            columns={columns}
            summary={summary}
          />
        }
      />
      {!readOnly && (
        <div ref={addWrapRef}>
          <AppButton className="productTable__add" icon={<PlusOutlined />} onClick={addRow}>
            Thêm dòng
          </AppButton>
        </div>
      )}
    </div>
  );
});

ProductTable.displayName = "ProductTable";
